import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { fetchSettings } from "../../Redux/WebsiteSlice";
import { useBookingModal } from "../../context/BookingModalContext";
import { locationsAPI, conditionsAPI, servicesAPI, getImageUrl } from "../../services/api";
import logo from "../../assets/img/logo web.webp";
import logo2 from "../../assets/img/logo-2.webp";

// Pages with no .page-hero backdrop — the header has nothing dark to sit on
// top of at scroll position 0, so it must always render in its solid/scrolled
// style on these routes instead of the default transparent-over-hero style.
const NO_HERO_EXACT_PATHS = ["/contact"];
const NO_HERO_PATH_PREFIXES = ["/locations/"];
const hasNoHero = (pathname) =>
  NO_HERO_EXACT_PATHS.includes(pathname) || NO_HERO_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const settings = useSelector((state) => state.website.settings);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [locations, setLocations] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [services, setServices] = useState([]);
  // Only one mobile accordion (services/conditions/locations) can be open at
  // a time — a single key instead of three independent booleans.
  const [openAccordion, setOpenAccordion] = useState(null);
  const toggleAccordion = (key) => setOpenAccordion((prev) => (prev === key ? null : key));

  // The desktop nav dropdowns open on CSS :hover. Clicking an item navigates
  // client-side (no page reload), so the mouse never actually leaves the nav
  // item and the menu stays open over the new page. This flag force-hides it
  // the moment an item is clicked, until the mouse leaves and re-enters.
  const [suppressDesktopDropdown, setSuppressDesktopDropdown] = useState(false);
  const closeDesktopDropdown = () => setSuppressDesktopDropdown(true);
  const resetDesktopDropdown = () => setSuppressDesktopDropdown(false);
  const desktopDropdownClass = suppressDesktopDropdown ? " nav-item-dropdown--suppressed" : "";
  const { openModal } = useBookingModal();

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (settings?.pageHeroColor) {
      document.documentElement.style.setProperty('--page-hero-bg', settings.pageHeroColor);
    }
  }, [settings?.pageHeroColor]);

  useEffect(() => {
    if (settings?.seo?.siteTitle) {
      document.title = settings.seo.siteTitle;
    }
  }, [settings?.seo?.siteTitle]);

  useEffect(() => {
    const pageHeroKeyByPath = {
      '/services': 'services',
      '/conditions': 'conditions',
      '/fees': 'fees',
      '/our-team': 'ourTeam',
      '/faq': 'faq',
      '/work-with-us': 'workWithUs',
      '/contact': 'contact',
      '/blog': 'blog',
    };
    const key = pageHeroKeyByPath[location.pathname];
    const image = key && settings?.pageHeroImages?.[key];

    const root = document.documentElement.style;
    if (image) {
      root.setProperty('--page-hero-bg-image', `url(${getImageUrl(image)})`);
      root.setProperty('--page-hero-overlay', 'linear-gradient(180deg, rgba(20, 28, 43, 0.55), rgba(20, 28, 43, 0.75))');
    } else {
      root.setProperty('--page-hero-bg-image', 'none');
      root.setProperty('--page-hero-overlay', 'none');
    }
  }, [location.pathname, settings?.pageHeroImages]);

  useEffect(() => {
    locationsAPI.getAll()
      .then(res => setLocations(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    conditionsAPI.getAll()
      .then(res => setConditions(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    servicesAPI.getAll()
      .then(res => setServices(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const servicesMenu = [
    { title: "PHYSIOTHERAPY SERVICES", link: "#physiotherapy" },
    { title: "SPORTS PHYSIOTHERAPY", link: "#sports" },
    { title: "JOINT REPLACEMENT REHABILITATION", link: "#joint-replacement" },
    { title: "JOINT MOBILISATION & MANIPULATION", link: "#joint-mobilisation" },
    { title: "EXERCISE REHAB", link: "#exercise-rehab" },
    { title: "CLINICAL PILATES & REFORMER PILATES", link: "#pilates" },
    { title: "DRY NEEDLING", link: "#dry-needling" },
  ];

  const conditionsMenu = [
    { title: "BACK PAIN", link: "#back-pain" },
    { title: "NECK PAIN", link: "#neck-pain" },
    { title: "SHOULDER PAIN", link: "#shoulder-pain" },
    { title: "KNEE PAIN", link: "#knee-pain" },
    { title: "HIP PAIN", link: "#hip-pain" },
    { title: "SPORTS INJURIES", link: "#sports-injuries" },
  ];

  const aboutMenu = [
    { title: "OUR TEAM", link: "#team" },
    { title: "OUR CLINIC", link: "#clinic" },
    { title: "TESTIMONIALS", link: "#testimonials" },
    { title: "FAQ", link: "#faq" },
  ];

  // Close mobile menu on route change. Desktop dropdown suppression is
  // intentionally NOT reset here — it must stay suppressed until the mouse
  // actually leaves the nav item, otherwise it would instantly reopen via
  // :hover since the click never moved the cursor away.
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenAccordion(null);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileMenuOpen]);

  // Handle section navigation with smart scrolling
  const handleSectionClick = (sectionId, e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    const currentPath = location.pathname;

    if (currentPath === "/") {
      // Already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    } else {
      // Navigate to home with hash, scroll will happen via useEffect in Home.js
      navigate(`/#${sectionId}`);
    }
  };

  const showSolidHeader = isScrolled || hasNoHero(location.pathname);
  const isPageVisible = (key) => settings?.pageVisibility?.[key] !== false;

  return (
    <>
      <header className={`header ${showSolidHeader ? "header--scrolled" : ""}`}>
        <div className="header__container">
          {/* Logo */}
          <div className="header__logo">
            <Link to="/" className="logo" id="logo__img logo__img--scrolled">
              <div className="logo__icon">
                <img
                  src={settings?.headerLogo || logo}
                  alt="Logo"
                  className="logo__img logo__img--default"
                  onError={(e) => {
                    e.target.src = logo;
                  }}
                />
                <img
                  src={settings?.headerLogo || logo}
                  alt="Logo"
                  className="logo__img logo__img--scrolled"
                  onError={(e) => {
                    e.target.src = logo;
                  }}
                />
              </div>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="header__nav">
            {/* <a href="#OurTeam" className="nav-link">
              Our Team
            </a> */}

            {/* <div className="nav-item-dropdown">
              <a href="#services" className="nav-link">
                SERVICES
                <svg
                  className="dropdown-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </a>
              <div className="dropdown-menu">
                {servicesMenu.map((item, index) => (
                  <a key={index} href={item.link} className="dropdown-item">
                    {item.title}
                  </a>
                ))}
              </div>
            </div>

            <div className="nav-item-dropdown">
              <a href="#conditions" className="nav-link">
                COMMON CONDITIONS
                <svg
                  className="dropdown-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </a>
              <div className="dropdown-menu">
                {conditionsMenu.map((item, index) => (
                  <a key={index} href={item.link} className="dropdown-item">
                    {item.title}
                  </a>
                ))}
              </div>
            </div>

            <div className="nav-item-dropdown">
              <a href="#about" className="nav-link">
                ABOUT
                <svg
                  className="dropdown-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </a>
              <div className="dropdown-menu">
                {aboutMenu.map((item, index) => (
                  <a key={index} href={item.link} className="dropdown-item">
                    {item.title}
                  </a>
                ))}
              </div>
            </div> */}

            {settings?.headerNavigation &&
            settings.headerNavigation.length > 0 ? (
              settings.headerNavigation.map((item, index) => {
                const isHash = item.link && item.link.startsWith("#");
                const isInternal = item.link && item.link.startsWith("/");
                if (isHash) {
                  const sectionId = item.link.substring(1);
                  return (
                    <a
                      key={index}
                      href={item.link}
                      className="nav-link"
                      onClick={(e) => handleSectionClick(sectionId, e)}
                    >
                      {item.title}
                    </a>
                  );
                }
                if (isInternal) {
                  return (
                    <NavLink key={index} to={item.link} className="nav-link">
                      {item.title}
                    </NavLink>
                  );
                }
                return (
                  <a key={index} href={item.link} className="nav-link" target="_blank" rel="noopener noreferrer">
                    {item.title}
                  </a>
                );
              })
            ) : (
              <>
                {isPageVisible('services') && (services.length > 0 ? (
                  <div className={`nav-item-dropdown${desktopDropdownClass}`} onMouseLeave={resetDesktopDropdown}>
                    <NavLink to="/services" className="nav-link" onClick={closeDesktopDropdown}>
                      Services
                      <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </NavLink>
                    <div className="dropdown-menu">
                      {services.map((service) => (
                        <NavLink key={service._id} to={`/services/${service.slug || service._id}`} className="dropdown-item" onClick={closeDesktopDropdown}>
                          {service.title}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ) : (
                  <NavLink to="/services" className="nav-link">Services</NavLink>
                ))}
                {isPageVisible('conditions') && (conditions.length > 0 ? (
                  <div className={`nav-item-dropdown${desktopDropdownClass}`} onMouseLeave={resetDesktopDropdown}>
                    <NavLink to="/conditions" className="nav-link" onClick={closeDesktopDropdown}>
                      Conditions
                      <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9"></polyline>
                      </svg>
                    </NavLink>
                    <div className="dropdown-menu">
                      {conditions.map((condition) => (
                        <NavLink key={condition._id} to={`/conditions/${condition.slug || condition._id}`} className="dropdown-item" onClick={closeDesktopDropdown}>
                          {condition.title}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ) : (
                  <NavLink to="/conditions" className="nav-link">Conditions</NavLink>
                ))}
                {isPageVisible('fees') && <NavLink to="/fees" className="nav-link">Fees</NavLink>}
                {isPageVisible('ourTeam') && <NavLink to="/our-team" className="nav-link">Our Team</NavLink>}
                {isPageVisible('faq') && <NavLink to="/faq" className="nav-link">FAQ</NavLink>}
                {isPageVisible('workWithUs') && <NavLink to="/work-with-us" className="nav-link">Work With Us</NavLink>}
                {isPageVisible('contact') && <NavLink to="/contact" className="nav-link">Contact</NavLink>}
              </>
            )}

            {locations.length > 0 && (
              <div className={`nav-item-dropdown${desktopDropdownClass}`} onMouseLeave={resetDesktopDropdown}>
                <a href="#locations" className="nav-link" onClick={e => e.preventDefault()}>
                  Locations
                  <svg className="dropdown-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </a>
                <div className="dropdown-menu">
                  {locations.map((loc) => (
                    <NavLink key={loc._id} to={`/locations/${loc.slug || loc._id}`} className="dropdown-item" onClick={closeDesktopDropdown}>
                      {loc.name}
                    </NavLink>
                  ))}
                </div>
              </div>
            )}
          </nav>

          {/* Right Section */}
          <div className="header__actions">
            {/* <a href="tel:0893680458" className="phone-link">
              <svg
                className="phone-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              (08) 9368 0458
            </a> */}
            <button type="button" onClick={() => setTimeout(openModal, 0)} className="btn-appointment">
              BOOK AN APPOINTMENT
            </button>
            <button
              className={`menu-toggle ${mobileMenuOpen ? "menu-toggle--active" : ""}`}
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div
        className={`mobile-overlay ${mobileMenuOpen ? "mobile-overlay--open" : ""}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <div className={`mobile-menu ${mobileMenuOpen ? "mobile-menu--open" : ""}`}>
        <nav className="mobile-menu__nav">
          {settings?.headerNavigation &&
          settings.headerNavigation.length > 0 ? (
            settings.headerNavigation.map((item, index) => {
              const isHash = item.link && item.link.startsWith("#");
              const isInternal = item.link && item.link.startsWith("/");
              if (isHash) {
                const sectionId = item.link.substring(1);
                return (
                  <a
                    key={index}
                    href={item.link}
                    className="mobile-menu__link"
                    onClick={(e) => handleSectionClick(sectionId, e)}
                  >
                    {item.title}
                  </a>
                );
              }
              if (isInternal) {
                return (
                  <NavLink
                    key={index}
                    to={item.link}
                    className="mobile-menu__link"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.title}
                  </NavLink>
                );
              }
              return (
                <a
                  key={index}
                  href={item.link}
                  className="mobile-menu__link"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.title}
                </a>
              );
            })
          ) : (
            <>
              {isPageVisible('services') && (services.length > 0 ? (
                <div className="mobile-menu__accordion">
                  <button
                    className="mobile-menu__accordion-toggle"
                    onClick={() => toggleAccordion('services')}
                  >
                    Services
                    <svg
                      className={`mobile-menu__accordion-icon${openAccordion === 'services' ? ' mobile-menu__accordion-icon--open' : ''}`}
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {openAccordion === 'services' && (
                    <div className="mobile-menu__accordion-items">
                      {services.map((service) => (
                        <NavLink
                          key={service._id}
                          to={`/services/${service.slug || service._id}`}
                          className="mobile-menu__sub-link"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {service.title}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink to="/services" className="mobile-menu__link" onClick={() => setMobileMenuOpen(false)}>Services</NavLink>
              ))}
              {isPageVisible('conditions') && (conditions.length > 0 ? (
                <div className="mobile-menu__accordion">
                  <button
                    className="mobile-menu__accordion-toggle"
                    onClick={() => toggleAccordion('conditions')}
                  >
                    Conditions
                    <svg
                      className={`mobile-menu__accordion-icon${openAccordion === 'conditions' ? ' mobile-menu__accordion-icon--open' : ''}`}
                      viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  {openAccordion === 'conditions' && (
                    <div className="mobile-menu__accordion-items">
                      {conditions.map((condition) => (
                        <NavLink
                          key={condition._id}
                          to={`/conditions/${condition.slug || condition._id}`}
                          className="mobile-menu__sub-link"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {condition.title}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <NavLink to="/conditions" className="mobile-menu__link" onClick={() => setMobileMenuOpen(false)}>Conditions</NavLink>
              ))}
              {isPageVisible('fees') && <NavLink to="/fees" className="mobile-menu__link" onClick={() => setMobileMenuOpen(false)}>Fees</NavLink>}
              {isPageVisible('ourTeam') && <NavLink to="/our-team" className="mobile-menu__link" onClick={() => setMobileMenuOpen(false)}>Our Team</NavLink>}
              {isPageVisible('faq') && <NavLink to="/faq" className="mobile-menu__link" onClick={() => setMobileMenuOpen(false)}>FAQ</NavLink>}
              {isPageVisible('workWithUs') && <NavLink to="/work-with-us" className="mobile-menu__link" onClick={() => setMobileMenuOpen(false)}>Work With Us</NavLink>}
              {isPageVisible('contact') && <NavLink to="/contact" className="mobile-menu__link" onClick={() => setMobileMenuOpen(false)}>Contact</NavLink>}
            </>
          )}

          {locations.length > 0 && (
            <div className="mobile-menu__accordion">
              <button
                className="mobile-menu__accordion-toggle"
                onClick={() => toggleAccordion('locations')}
              >
                Locations
                <svg
                  className={`mobile-menu__accordion-icon${openAccordion === 'locations' ? ' mobile-menu__accordion-icon--open' : ''}`}
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              {openAccordion === 'locations' && (
                <div className="mobile-menu__accordion-items">
                  {locations.map((loc) => (
                    <NavLink
                      key={loc._id}
                      to={`/locations/${loc.slug || loc._id}`}
                      className="mobile-menu__sub-link"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {loc.name}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>
        <button
          onClick={() => { setMobileMenuOpen(false); setTimeout(() => openModal(), 150); }}
          className="mobile-menu__btn"
        >
          BOOK AN APPOINTMENT
        </button>
      </div>
    </>
  );
};

export default Header;
